import React from 'react'
import {api, apiErrorMessage} from "js/studios/shared/api"
import {trackEventSP} from 'js/studios/shared/tracking'
import * as Sentry from "@sentry/react"
import SpinnerSvg from "images/spinner.svg"

/******** General Auth *****/

/**
 * Sends ajax request to our RegModal API
 * @return {jQuery.promise}
 */
export function sendRequest(method, data) {
  data = {...data, _method: method}
  return api.post(window.cm.urls['reg-modal-api'], data).then(resp => {
    if (['login', 'signupFull', 'facebookLogin', 'googleLogin', 'appleLogin'].includes(method)) {
      window.cm.store.dispatch({type: 'USER_LOGIN', user: resp})
      document.querySelector('[name="csrf-token"]').setAttribute('content', resp.csrf_token)
    }
    return resp
  }).catch(err => {throw apiErrorMessage(err)})
}


/******** Facebook *********/

/**
 * Inner function to check facebook status. Needed as we need to recursively attempt, potentially waiting for facebook to load.
 */
function _checkFacebookStatus(attempt, resolve, reject) {
  if (!window.FB) {
    trackEventSP('wait_facebook_not_loaded', 'Customer sign up', {attempt: attempt})
    if (attempt > 3) {
      trackEventSP('could_not_load_facebook_successfully', 'Customer sign up', {attempt: attempt})
      reject("Could not reach facebook. Please refresh the page and try again.")
      return null  // do not need to proceed any further
    } else {
      window.setTimeout(() => _checkFacebookStatus(attempt + 1, resolve, reject), 1000) // recursive call to try again (why we need the inner function)
      return null  // do not need to proceed any further
    }
  } else if (attempt > 1) {
    trackEventSP('facebook_successfully_loaded', 'Customer sign up', {attempt: attempt})
  }

  window.FB.getLoginStatus(response => {
    if (response.status === "connected") {
      resolve(response)
    } else {
      reject()
    }
  })
}

/**
 * Checks to see if user is already logged into facebook
 * @return {Promise}
 */
export function checkFacebookStatus() {
  return new Promise((resolve, reject) => _checkFacebookStatus(1, resolve, reject))
}


/**
 * Sends FB creds to our server, so that the user is logged into our systems
 * @return {Promise}
 */
function sendFbCreds(response) {
  const authResponse = response.authResponse
  const data = {
    'uid': authResponse.userID,
    'accessToken': authResponse.accessToken,
    'agreement': true,
  }
  return sendRequest('facebookLogin', data)
}

/**
 * Pops a facebook login dialog for users to login. It also queries the facebook graph api
 * to retrieve their profile data. This function works for both users who have approved our app
 * and new users.
 *
 * @returns { jQuery promise}
 */
function connectFacebook() {
  return new Promise((resolve, reject) => {
    window.FB.login(response => {
      if (response.authResponse) {
        window.FB.api('/me', () => {
          resolve(response); // send ORIGINAL response from fb login. The call to fb '/me' is just making sure that we have valid creds
        })
      } else {
        reject()
      }
    }, {scope: 'public_profile,email'})
  })
}

/**
 * Logs in a facebook user. There are quite a few
 * api calls that are required -- checking facebook auth, fetching fb data from facebook graph, sending
 * fb creds to our servers -- so we utilize promises to make things a bit more straightforward.
 * @return {jQuery promise}
 */
export function loginFacebook() {
  return new Promise((resolve, reject) => {
    // first check to see if they're already logged into facebook
    checkFacebookStatus()
      .then(response => {
        // user is logged in, so just send fb info to server
        sendFbCreds(response)
          .then(resp => resolve(resp))
          .catch(() => {
            reject("Failed to use facebook credentials to login to our website")
          })
      })
      .catch(error => {
        // not logged in yet, so connect to facebook. Note this handles registration for new users
        // as well
        if (error) {
          reject(error)
        } else {
          connectFacebook()
            .then(response => {
              sendFbCreds(response)
                .then(resp => resolve(resp))
                .catch(() => {
                  reject("Failed to use facebook credentials to login to our website")
                })
            })
            .catch(() => {
              reject("Failed to retrieve your facebook credentials")
            })
         }
      })
  })
}

export function googleLoginAvailable() {
  return window._google_oauth_client_id && window.google && window.google.accounts
}

export class GoogleLoginButton extends React.Component {
  state = {loading: false}

  componentDidMount() {
    const redirectUri = new URL(window.location)
    redirectUri.pathname = window._google_oauth_redirect_path
    window.google.accounts.id.initialize({
      client_id: window._google_oauth_client_id,
      callback: this.googleCallback,
      login_uri: redirectUri.toString(),
    })
    window.google.accounts.id.renderButton(this.btnContainer, {
      type: "standard",
      theme: "filled_blue",
      size: "large",
      shape: "pill",
      text: this.props.signup ? "signup_with" : "signin_with",
      width: this.btnContainer.offsetWidth,
    })
  }

  // wraps setting this state to loading, running the handler with given data,
  // and then un-setting the loading state when done
  runHandler(data) {
    Promise.all([this.props.handler(data)]).finally(
      () => this.setState({loading: false})
    )
  }

  googleCallback = (credResp) => {
    if (!credResp.clientId || !credResp.credential) {
      this.runHandler({ error: "Google login failed" })
    }

    this.setState({loading: true})
    sendRequest('googleLogin', {oauthCredential: credResp.credential})
      .then(resp => this.runHandler(resp))
      .catch(errorMsg => this.runHandler({ error: errorMsg }))
  }

  render() {
    return <div className="google-login-btn">
      <div ref={(el) => { this.btnContainer = el } } style={this.state.loading ? {display: "none"} : {}} />
      {this.state.loading &&
        <div className="loading-spinner"><SpinnerSvg /></div>
      }
    </div>
  }
}

export function appleSigninAvailable() {
  return window.AppleID !== undefined
}

// map the error code on ev.detail.error to a human error message
const APPLE_ERRORS = {
  "popup_closed_by_user": "The Sign in with Apple popup was closed",
}

export class AppleSigninButton extends React.Component {
  state = {loading: false}

  componentDidMount() {
    window.AppleID.auth.renderButton()

    document.addEventListener("AppleIDSignInOnSuccess", this.signInSuccessHandler)
    document.addEventListener("AppleIDSignInOnFailure", this.signInErrorHandler)
  }

  componentWillUnmount() {
    document.removeEventListener("AppleIDSignInOnSuccess", this.signInSuccessHandler)
    document.removeEventListener("AppleIDSignInOnFailure", this.signInErrorHandler)
  }

  runHandler(data) {
    Promise.all([this.props.handler(data)]).finally(
      () => this.setState({loading: false})
    )
  }

  signInSuccessHandler = ev => {
    this.setState({loading: true})
    // ev.detail should match https://developer.apple.com/documentation/sign_in_with_apple/signinresponsei
    // It should always contain `authorization` (the JWT token details), and for
    // initial signups should also contain `user` (with user name details)
    sendRequest("appleLogin", ev.detail)
      .then(resp => this.runHandler(resp))
      .catch(errorMsg => this.runHandler({error: errorMsg}))
  }

  signInErrorHandler = ev => {
    this.setState({loading: true})
    if (APPLE_ERRORS[ev.detail && ev.detail.error]) {
      this.runHandler({ error: APPLE_ERRORS[ev.detail.error] })
    } else { // fallback for unhandled error types
      Sentry.withScope(function(scope) {
          scope.setExtras({ detail: ev.detail })
          Sentry.captureMessage(`Apple Sign In error: ${ev.detail && ev.detail.error}`, "error")
      })
      this.runHandler({ error: "An error occurred" })
    }
  }

  render() {
    return <div className="apple-login-btn">
      <a id="appleid-signin"
        data-color="black"
        data-border="true"
        data-type={this.props.signup ? "sign-up" : "sign-in"}
        data-height={"40px" /* 2.5rem to match btn--fb */}
        style={this.state.loading ? {display: "none"} : {}}
        ></a>
      {this.state.loading &&
        <div className="loading-spinner"><SpinnerSvg /></div>
      }
    </div>
  }
}
